import React, { useEffect, useState } from 'react';
import Card from '../components/Card';
import TabList from '../components/TabList';
import Button from '../components/Button';
import * as demandApi from '../services/demandSensingApi';

const TYPE_TABS = [
  { key: '', label: 'All Signals' },
  { key: 'signal', label: 'Demand Signals' },
  { key: 'pattern', label: 'Pattern' },
  { key: 'trend', label: 'Market Trend' },
  { key: 'change', label: 'Change Detection' },
  { key: 'seasonal', label: 'Seasonal' },
];

const DIRECTION_ICON = { up: '\u2191', down: '\u2193', volatile: '\u2195', stable: '\u2192' };

function SeverityBadge({ severity }) {
  const colors = { high: 'var(--color-danger)', medium: 'var(--color-warning)', low: 'var(--color-text-muted)' };
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 700,
        color: '#fff',
        background: colors[severity] || 'var(--color-text-muted)',
        borderRadius: 4,
        padding: '2px 8px',
        textTransform: 'uppercase',
      }}
    >
      {severity}
    </span>
  );
}

export default function DemandSensingPage() {
  const [type, setType] = useState('');
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastRun, setLastRun] = useState(null);

  const load = (t) => demandApi.listSignals(t ? { signalType: t } : {}).then(setSignals);

  useEffect(() => {
    setLoading(true);
    load(type)
      .catch((err) => setError(err.message || 'Could not load demand signals.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const result = await demandApi.refreshSignals();
      setLastRun(result);
      await load(type);
    } catch (err) {
      setError(err.message || 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Demand Sensing</h1>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            AI-detected patterns, seasonality, market trends, and significant demand shifts
            from order history.
          </p>
        </div>
        <Button variant="secondary" onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? 'Analyzing…' : 'Run demand sensing'}
        </Button>
      </div>

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {lastRun && (
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
          Last run found {lastRun.signalCount} signal(s)
          {lastRun.seasonalAnalysis && lastRun.seasonalAnalysis.skipped
            ? ' (seasonal analysis needs at least 3 months of order history — not enough yet).'
            : '.'}
        </p>
      )}

      <TabList tabs={TYPE_TABS} activeKey={type} onChange={setType} ariaLabel="Signal type" />

      {loading ? (
        <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {signals.map((s) => (
            <Card key={s.signal_id} style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    {s.direction && (
                      <span style={{ fontSize: 'var(--font-size-lg)', color: s.direction === 'up' ? 'var(--color-success)' : s.direction === 'down' ? 'var(--color-danger)' : 'var(--color-warning)' }}>
                        {DIRECTION_ICON[s.direction]}
                      </span>
                    )}
                    <strong style={{ fontSize: 'var(--font-size-base)' }}>{s.title}</strong>
                  </div>
                  <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>{s.description}</p>
                  <div style={{ marginTop: 6, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    {s.design_name && <span>{s.design_name} · </span>}
                    {s.confidence && <span>confidence {Math.round(s.confidence * 100)}% · </span>}
                    <span>{s.signal_type}</span>
                  </div>
                </div>
                <SeverityBadge severity={s.severity} />
              </div>
            </Card>
          ))}
          {signals.length === 0 && (
            <Card>
              <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>
                No signals in this category yet. Run demand sensing after importing order data,
                or broaden the filter.
              </p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
